/**
 * AnyGRC Unified Form Components — Self-Contained API
 * ============================================================================
 * USAGE PRINCIPLE: Pass label/error/helper directly to the component.
 * Components handle their own wrapper, label, error, and helper text.
 * Pages never touch CSS classes or HTML form elements directly.
 *
 * USAGE:
 *   <Input  label="Email" required type="email" value={v} onChange={fn} error={errors.email} />
 *   <Select label="Role"  value={v} onChange={fn} options={opts} />
 *   <Textarea label="Notes" value={v} onChange={fn} rows={4} />
 *   <SearchSelect label="Category" options={opts} value={v} onChange={fn} clearable />
 *   <Checkbox label="Active" checked={v} onChange={fn} />
 *   <RadioGroup name="type" label="Type" options={opts} value={v} onChange={fn} />
 *
 * For complex custom layouts, <FormGroup> is still exported as an escape hatch.
 */

import React, {
  forwardRef,
  useId,
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  useCallback,
  KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { formatDateBySetting, toDatePickerFormat, toDatePlaceholder } from '../../utils/dateSettings';
import {
  Eye, EyeOff, Search, ChevronDown, Check, AlertCircle, X, Upload, Calendar, Clock
} from 'lucide-react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

// ── Utility ──────────────────────────────────────────────────────────────────

function cx(...classes: (string | false | null | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

// ── Shared field-wrapper props (every component accepts these) ────────────────

interface FieldWrapperProps {
  /** Rendered as a <label> above the control */
  label?: string;
  /** Appends a red asterisk to the label */
  required?: boolean;
  /** Error message — shown below control in red; also activates error border */
  error?: string | boolean;
  /** Helper text displayed below the field */
  helper?: React.ReactNode;
  /** Alias for helper text */
  helperText?: React.ReactNode;
  /** If true, removes the default bottom margin */
  noMargin?: boolean;
  /** If true, renders the label and control inline */
  inline?: boolean;
  /** Custom class for the wrapper */
  className?: string;
  /** Custom style for the wrapper */
  style?: React.CSSProperties;
}

// ── Internal wrapper (not exported — use FormGroup for custom layouts) ─────────

const FieldWrapper: React.FC<FieldWrapperProps & { id?: string; children: React.ReactNode }> = ({
  label, required, error, helper, helperText, noMargin, inline, className, id, children,
}) => {
  const actualHelper = helper || helperText;
  return (
    <div className={cx('fg', inline && 'fg--inline', noMargin && 'fg--no-margin', className)}>
      {label && (
        <label className="fg__label" htmlFor={id}>
          {label}
          {required && <span className="fg__required" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="fg__error" role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          {error}
        </span>
      ) : actualHelper ? (
        <span className="fg__helper">{actualHelper}</span>
      ) : null}
    </div>
  );
};

// ── Size helper ───────────────────────────────────────────────────────────────

type SizeVariant = 'sm' | 'md' | 'lg';

function sizeClass(size?: SizeVariant): string {
  if (size === 'sm') return 'fi--sm';
  if (size === 'lg') return 'fi--lg';
  return '';
}

// ═════════════════════════════════════════════════════════════════════════════
// FormGroup — escape hatch for custom layouts
// ═════════════════════════════════════════════════════════════════════════════

export interface FormGroupProps extends FieldWrapperProps {
  htmlFor?: string;
  inline?: boolean;
  children: React.ReactNode;
}

/**
 * Low-level wrapper — only use when the higher-level components can't express
 * the layout you need (e.g. side-by-side label + control, dynamic field arrays).
 */
export const FormGroup = forwardRef<HTMLDivElement, FormGroupProps>(
  ({ label, required, error, helper, inline, className, style, children, htmlFor }, ref) => (
    <div ref={ref} className={cx('fg', inline && 'fg--inline', className)} style={style}>
      {label && (
        <label className="fg__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="fg__required" aria-hidden="true">*</span>}
        </label>
      )}
      {children}
      {error ? (
        <span className="fg__error" role="alert">
          <AlertCircle size={12} aria-hidden="true" />
          {error}
        </span>
      ) : helper ? (
        <span className="fg__helper">{helper}</span>
      ) : null}
    </div>
  )
);
FormGroup.displayName = 'FormGroup';

// ═════════════════════════════════════════════════════════════════════════════
// Input
// ═════════════════════════════════════════════════════════════════════════════

export interface InputProps
  extends FieldWrapperProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'className'> {
  size?: SizeVariant;
  /** Leading Lucide icon element */
  leadingIcon?: React.ReactNode;
  /** Alias for leadingIcon */
  icon?: React.ReactNode;
  /** Trailing Lucide icon or element */
  trailingIcon?: React.ReactNode;
  /** Trailing text addon (e.g. "kg", ".com") */
  addon?: string;
}

/**
 * Unified text input — covers text, email, password, date, number, search, etc.
 * Password type automatically gets a show/hide toggle.
 *
 * @example
 * <Input label="Email" required type="email" name="email" value={v} onChange={fn} error={errors.email} />
 * <Input label="Password" type="password" name="pass" value={v} onChange={fn} />
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label, required, error, helper, helperText, noMargin, inline, className,
      type = 'text', size, leadingIcon, icon, trailingIcon, addon,
      id: propId,
      autoComplete = 'off',
      ...props
    },
    ref
  ) => {
    const autoId = useId();
    const id = propId ?? autoId;
    const [showPw, setShowPw] = useState(false);

    const actualLeadingIcon = leadingIcon || icon;
    const inputClass = cx('fi', 'fi--text', sizeClass(size), error && 'fi--error');

    let control: React.ReactNode;

    if (type === 'password') {
      control = (
        <div className="fi-pw-wrap">
          <input
            ref={ref}
            id={id}
            type={showPw ? 'text' : 'password'}
            className={inputClass}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? `${id}-err` : (helper || helperText) ? `${id}-hint` : undefined}
            autoComplete={autoComplete}
            {...props}
          />
          <button
            type="button"
            className="fi-pw-toggle"
            onClick={() => setShowPw(p => !p)}
            tabIndex={-1}
            aria-label={showPw ? 'Hide password' : 'Show password'}
          >
            {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      );
    } else if (actualLeadingIcon || trailingIcon) {
      control = (
        <div className={cx('fi-icon-wrap', !!trailingIcon && 'fi-icon-wrap--trailing')}>
          {actualLeadingIcon && <span className="fi-icon" aria-hidden="true">{actualLeadingIcon}</span>}
          <input
            ref={ref}
            id={id}
            type={type}
            className={inputClass}
            aria-invalid={error ? 'true' : undefined}
            autoComplete={autoComplete}
            {...props}
          />
          {trailingIcon && <span className="fi-icon fi-icon--trailing" aria-hidden="true">{trailingIcon}</span>}
          {addon && <span className="fi-addon">{addon}</span>}
        </div>
      );
    } else if (addon) {
      control = (
        <div className="fi-addon-wrap">
          <input ref={ref} id={id} type={type} className={inputClass} aria-invalid={error ? 'true' : undefined} autoComplete={autoComplete} {...props} />
          <span className="fi-addon">{addon}</span>
        </div>
      );
    } else {
      control = (
        <input
          ref={ref}
          id={id}
          type={type}
          className={inputClass}
          aria-invalid={error ? 'true' : undefined}
          aria-describedby={error ? `${id}-err` : (helper || helperText) ? `${id}-hint` : undefined}
          autoComplete={autoComplete}
          {...props}
        />
      );
    }

    return (
      <FieldWrapper 
        label={label} 
        required={required} 
        error={error} 
        helper={helper} 
        helperText={helperText}
        noMargin={noMargin}
        inline={inline}
        id={id} 
        className={className}
      >
        {control}
      </FieldWrapper>
    );
  }
);
Input.displayName = 'Input';

// ═════════════════════════════════════════════════════════════════════════════
// Textarea
// ═════════════════════════════════════════════════════════════════════════════

export interface TextareaProps
  extends FieldWrapperProps,
    Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className'> {
  noResize?: boolean;
}

/**
 * @example
 * <Textarea label="Description" value={v} onChange={fn} rows={4} error={errors.desc} />
 */
export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, required, error, helper, className, noResize, id: propId, ...props }, ref) => {
    const autoId = useId();
    const id = propId ?? autoId;
    return (
      <FieldWrapper label={label} required={required} error={error} helper={helper} id={id} className={className}>
        <textarea
          ref={ref}
          id={id}
          className={cx('fi', 'fi--textarea', noResize && 'fi--textarea--no-resize', error && 'fi--error')}
          aria-invalid={error ? 'true' : undefined}
          {...props}
        />
      </FieldWrapper>
    );
  }
);
Textarea.displayName = 'Textarea';

// ═════════════════════════════════════════════════════════════════════════════
// Select
// ═════════════════════════════════════════════════════════════════════════════

export interface SelectProps
  extends FieldWrapperProps,
    Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size' | 'className'> {
  size?: SizeVariant;
  /** Leading Lucide icon element */
  leadingIcon?: React.ReactNode;
}

/**
 * @example
 * <Select label="Gender" name="gender" value={v} onChange={fn}>
 *   <option value="">Select…</option>
 *   <option value="Male">Male</option>
 * </Select>
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, required, error, helper, helperText, noMargin, inline, className, size, leadingIcon, id: propId, children, ...props }, ref) => {
    const autoId = useId();
    const id = propId ?? autoId;

    const selectClass = cx('fi', 'fi--select', sizeClass(size), error && 'fi--error');

    let control: React.ReactNode = (
      <select
        ref={ref}
        id={id}
        className={selectClass}
        aria-invalid={error ? 'true' : undefined}
        {...props}
      >
        {children}
      </select>
    );

    if (leadingIcon) {
      control = (
        <div className="fi-icon-wrap">
          <span className="fi-icon" aria-hidden="true">{leadingIcon}</span>
          {control}
        </div>
      );
    }

    return (
      <FieldWrapper 
        label={label} 
        required={required} 
        error={error} 
        helper={helper} 
        helperText={helperText}
        noMargin={noMargin}
        inline={inline}
        id={id} 
        className={className}
      >
        {control}
      </FieldWrapper>
    );
  }
);
Select.displayName = 'Select';

// ═════════════════════════════════════════════════════════════════════════════
// SearchSelect
// ═════════════════════════════════════════════════════════════════════════════

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SearchSelectProps extends FieldWrapperProps {
  options: SelectOption[];
  value?: string | number | null;
  onChange?: (value: string | number | null) => void;
  placeholder?: string;
  disabled?: boolean;
  'data-testid'?: string;
  id?: string;
  name?: string;
  clearable?: boolean;
  size?: SizeVariant;
  /** Hide the leading search icon — compact mode for filter bars */
  noSearchIcon?: boolean;
  /**
   * Label for the in-list "clear selection" row shown at the top of the open
   * panel when `clearable` is set and a value is selected. Defaults to the
   * `placeholder` (e.g. "All Sources"). Set to a custom string like "— None —".
   */
  clearLabel?: string;
}

/**
 * Searchable select with keyboard navigation (↑↓ Enter Escape), highlighted
 * results, optional clear button, and role-theme-aware focus ring.
 *
 * @example
 * <SearchSelect label="Category" options={opts} value={v} onChange={fn} clearable error={errors.cat} />
 */
export const SearchSelect: React.FC<SearchSelectProps> = ({
  label, required, error, helper, helperText, noMargin, inline, className,
  options, value, onChange, placeholder = 'Select…',
  disabled, id: propId, name, clearable, size, noSearchIcon, clearLabel,
  'data-testid': dataTestId,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  // -1 = no row highlighted. The highlight is a keyboard-nav cursor, NOT a
  // selection — defaulting it to 0 made the first option always look pre-selected
  // when the panel opened. It now lands on the actually-selected option (set in
  // openPanel) or stays at -1 when nothing is selected.
  const [highlighted, setHighlighted] = useState(-1);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Preferred trigger width = widest option label, capped at TRIGGER_MAX_WIDTH so a
  // long label (e.g. "Chief Information Security Officer (CISO)") wraps to a 2nd line
  // via .fi-ss-trigger__label instead of widening the control/panel. Applied as
  // `width` (not `min-width`) with a small `minWidth` floor so the trigger can SHRINK
  // on narrow/responsive viewports (CSS flex-shrink) and the toolbar can reflow.
  // Keep TRIGGER_MAX_WIDTH in sync with .fi-ss-trigger { max-width } in form.css.
  const TRIGGER_MAX_WIDTH = 160;
  const TRIGGER_MIN_WIDTH = 64; // floor so it never collapses below "All …" + chevron
  const triggerStyle = noSearchIcon ? (() => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;
    ctx.font = '13px "Google Sans", system-ui, sans-serif';
    const maxPx = Math.max(...options.map(o => ctx.measureText(o.label).width));
    // +10px left pad + 8px right pad + 16px chevron + 8px gap
    const preferred = Math.min(Math.ceil(maxPx) + 42, TRIGGER_MAX_WIDTH);
    return { width: preferred, minWidth: TRIGGER_MIN_WIDTH } as React.CSSProperties;
  })() : undefined;

  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const update = () => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      const MARGIN = 8;
      const PANEL_MAX = 280;
      // Measure room against the enclosing modal body (if any) rather than the raw
      // viewport — a centred modal has dark backdrop below it, so viewport-based
      // space lies and the panel opens down into the footer/backdrop. Fall back to
      // the viewport outside modals.
      // For dropdowns in modals, measure against viewport, not modal body.
      // The panel is portaled to document.body, so it can overflow the modal body
      // and scroll independently. Let the portal overflow naturally.
      const spaceBelow = window.innerHeight - r.bottom - MARGIN;
      const spaceAbove = r.top - MARGIN;
      // Open upward only if there's genuinely insufficient space below
      // AND there's more space above. This allows the panel to overflow the
      // modal body when there's viewport space below (and it will scroll).
      const openUp = spaceBelow < 120 && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(PANEL_MAX, openUp ? spaceAbove : spaceBelow));
      // min-width 180px so narrow filter-status triggers still get readable panels
      const panelWidth = Math.max(r.width, 180);
      // Clamp horizontally so the panel never spills past the viewport edge. A
      // right-aligned trigger (e.g. a filter at the far right of a toolbar) would
      // otherwise open a wider panel off the right side and get clipped. Prefer
      // left-aligning to the trigger, but if that overflows the right edge, shift
      // left so the panel's right edge sits MARGIN inside the viewport. Never go
      // past the left edge either.
      const left = Math.max(
        MARGIN,
        Math.min(r.left, window.innerWidth - panelWidth - MARGIN)
      );
      // Panel must sit ABOVE any modal overlay. The app modal overlay is z-index
      // 9999 (styles/modal.css) and the chatbot overlay is 10000; a tie would let
      // the modal paint over the portaled dropdown, leaving it invisible. Stay above both.
      const base: React.CSSProperties = { position: 'fixed', left, width: panelWidth, right: 'auto', zIndex: 10050, maxHeight };
      // The base CSS (.fi-search-select__panel) sets `top: calc(100% + 4px)`. When
      // opening UPWARD we anchor with `bottom`, so the leftover CSS `top` would pin
      // the panel a full viewport-height below and push it off-screen. Each branch
      // must explicitly null out the axis it does NOT use (top:'auto' / bottom:'auto').
      setPanelStyle(openUp
        ? { ...base, top: 'auto', bottom: window.innerHeight - r.top + 4 }
        : { ...base, bottom: 'auto', top: r.bottom + 4 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  const normalizedValue = value == null ? '' : String(value);
  const selected = options.find(o => String(o.value) === normalizedValue);
  const displayLabel = selected?.label ?? (noSearchIcon ? (options[0]?.label ?? placeholder) : '');
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useEffect(() => {
    if (!open) return; // Only register handler when dropdown is open

    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      // Check if click is inside the trigger container
      const isInsideContainer = containerRef.current?.contains(target);
      if (isInsideContainer) return; // Clicked on trigger or input, keep it open

      // Check if click is inside any search-select panel (find by class, not ref)
      const panel = (target as Element)?.closest?.('.fi-search-select__panel');
      if (panel) return; // Clicked inside panel, keep it open

      // Click was outside both — close the dropdown and stop propagation
      // so the click doesn't bubble to parent modals or overlays
      e.stopPropagation();
      setOpen(false);
      setQuery('');
    };

    // Use capture phase to ensure we catch clicks before other handlers
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open]);

  // While the user is typing a query, highlight the first match so Enter picks
  // it. With no query (panel just opened / query cleared) leave the highlight to
  // openPanel, which points it at the selected option (or -1 for none).
  useEffect(() => {
    if (query.trim()) setHighlighted(0);
  }, [filtered.length, query]);

  const openPanel = useCallback(() => {
    if (disabled) return;
    setOpen(true); setQuery('');
    // Land the keyboard cursor on the currently-selected option (or -1 = none)
    // so opening the panel doesn't make the first row look pre-selected.
    setHighlighted(options.findIndex(o => String(o.value) === normalizedValue));
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [disabled, options, normalizedValue]);

  const selectOption = useCallback((opt: SelectOption) => {
    onChange?.(opt.value); setOpen(false); setQuery('');
  }, [onChange]);

  const clearSelection = useCallback(() => {
    onChange?.(null); setOpen(false); setQuery('');
  }, [onChange]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlighted(h => Math.min(h + 1, filtered.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlighted(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') { e.preventDefault(); if (filtered[highlighted]) selectOption(filtered[highlighted]); }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };

  useEffect(() => {
    if (!listRef.current || highlighted < 0) return;
    // A dummy placeholder <li> is prepended as children[0] when not searching,
    // so the option at filtered[highlighted] is offset by one in the DOM.
    const domOffset = query.trim() ? 0 : 1;
    (listRef.current.children[highlighted + domOffset] as HTMLElement)?.scrollIntoView({ block: 'nearest' });
  }, [highlighted, query]);

  return (
    <FieldWrapper 
      label={label} 
      required={required} 
      error={error} 
      helper={helper} 
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      id={id} 
      className={className}
    >
      <div ref={containerRef} className={cx('fi-search-select', open && 'fi-search-select--open', noSearchIcon && 'fi-search-select--no-icon')}>
        {name && <input type="hidden" name={name} value={value ?? ''} />}
        <div className="fi-search-select__input-wrap">
          {noSearchIcon ? (
            /* Compact filter trigger — div so it sizes to content, not fixed input width */
            <div
              id={id}
              role="combobox"
              tabIndex={disabled ? -1 : 0}
              data-testid={dataTestId}
              className={cx('fi-ss-trigger', sizeClass(size), error && 'fi--error', disabled && 'fi-ss-trigger--disabled')}
              style={triggerStyle}
              onClick={() => {
                if (disabled) return;
                setOpen(o => {
                  // On open, point the keyboard cursor at the selected option
                  // (or -1 = none) so the first row isn't pre-highlighted.
                  if (!o) setHighlighted(options.findIndex(opt => String(opt.value) === normalizedValue));
                  return !o;
                });
              }}
              onKeyDown={handleKeyDown as any}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-invalid={error ? 'true' : undefined}
            >
              <span className="fi-ss-trigger__label">{displayLabel || placeholder}</span>
              <ChevronDown size={13} className={cx('fi-ss-trigger__chevron', open && 'fi-ss-trigger__chevron--open')} />
            </div>
          ) : (
            <>
              <span className="fi-search-select__icon" aria-hidden="true"><Search size={14} /></span>
              <input
                ref={inputRef}
                id={id}
                type="text"
                data-testid={dataTestId}
                className={cx('fi', 'fi--text', 'fi-search-select__input', sizeClass(size), error && 'fi--error')}
                value={open ? query : displayLabel}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={!open}
                // Toggle on mousedown (the single source of truth for pointer
                // interaction). mousedown fires BEFORE focus, so handling it here
                // and preventing default stops the old onClick+onFocus pair from
                // racing — that pair could open on focus then immediately close on
                // click (or vice-versa) on a fast click. We refocus manually when
                // opening so typing/search still works.
                onMouseDown={e => {
                  if (disabled) return;
                  e.preventDefault();
                  if (open) { setOpen(false); setQuery(''); }
                  else { openPanel(); }
                }}
                onChange={e => { setQuery(e.target.value); setHighlighted(0); }}
                onKeyDown={handleKeyDown}
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-autocomplete="list"
                aria-invalid={error ? 'true' : undefined}
                autoComplete="off"
              />
              <span className="fi-search-select__chevron" aria-hidden="true">
                {clearable && selected ? (
                  <button
                    type="button"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 0 }}
                    onMouseDown={e => { e.preventDefault(); e.stopPropagation(); clearSelection(); }}
                    onClick={e => { e.stopPropagation(); }}
                    aria-label="Clear selection"
                    data-testid={dataTestId ? `${dataTestId}__clear` : undefined}
                  >
                    <X size={14} />
                  </button>
                ) : <ChevronDown size={14} />}
              </span>
            </>
          )}
        </div>
        {open && createPortal(
          <div ref={panelRef} className="fi-search-select__panel" role="listbox" style={panelStyle}>
            <ul ref={listRef} className="fi-search-select__list">
              {/* Dummy placeholder row — always the first item when not searching,
                  mirroring a native <select>'s empty first <option>. Muted, picks
                  nothing; clicking it clears any current selection. */}
              {!query.trim() && (
                <li
                  role="option"
                  aria-selected={!selected}
                  data-testid={dataTestId ? `${dataTestId}__opt_clear` : undefined}
                  className="fi-search-select__option fi-search-select__option--clear"
                  onMouseDown={e => { e.preventDefault(); clearSelection(); }}
                >
                  <span>{clearLabel ?? placeholder}</span>
                </li>
              )}
              {filtered.length === 0 ? (
                <li className="fi-search-select__empty">No results found</li>
              ) : filtered.map((opt, idx) => (
                <li
                  key={opt.value}
                  role="option"
                  data-testid={`${dataTestId ? dataTestId + '__' : ''}opt_${String(opt.label).toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '')}`}
                  aria-selected={String(opt.value) === normalizedValue}
                  className={cx(
                    'fi-search-select__option',
                    idx === highlighted && 'fi-search-select__option--highlighted',
                    String(opt.value) === normalizedValue && 'fi-search-select__option--selected'
                  )}
                  onMouseDown={e => { e.preventDefault(); selectOption(opt); }}
                  onMouseEnter={() => setHighlighted(idx)}
                >
                  <span>{opt.label}</span>
                  {String(opt.value) === normalizedValue && (
                    <span className="fi-check-icon" aria-hidden="true"><Check size={12} /></span>
                  )}
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
      </div>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Checkbox
// ═════════════════════════════════════════════════════════════════════════════

export interface CheckboxProps extends FieldWrapperProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  indeterminate?: boolean;
  id?: string;
  name?: string;
  value?: string;
  'data-testid'?: string;
}

/**
 * Custom-styled checkbox. Supports indeterminate for "select all" patterns.
 *
 * @example
 * <Checkbox label="Is Active" checked={v} onChange={fn} />
 */
export const Checkbox: React.FC<CheckboxProps> = ({
  checked, onChange, disabled, indeterminate, label, required,
  id: propId, name, value, error, helper, helperText, noMargin, inline, className,
  'data-testid': dataTestId,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <FieldWrapper 
      label="" 
      required={required} 
      error={error} 
      helper={helper} 
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
    >
      <label htmlFor={id} className={cx('fi-checkbox', disabled && 'fi-checkbox--disabled')}>
        <input
          ref={inputRef}
          type="checkbox"
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={e => onChange?.(e.target.checked)}
          className="fi-checkbox__input"
          aria-invalid={error ? 'true' : undefined}
          data-testid={dataTestId}
        />
        <span className="fi-checkbox__box" aria-hidden="true">
          <Check size={10} strokeWidth={3} />
        </span>
        {label && <span className="fi-checkbox__label">{label}</span>}
      </label>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Radio
// ═════════════════════════════════════════════════════════════════════════════

export interface RadioProps {
  checked?: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  label?: string;
  id?: string;
  name?: string;
  value: string;
  className?: string;
}

export const Radio: React.FC<RadioProps> = ({
  checked, onChange, disabled, label, id: propId, name, value, className,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;
  return (
    <label htmlFor={id} className={cx('fi-radio', disabled && 'fi-radio--disabled', className)}>
      <input
        type="radio"
        id={id}
        name={name}
        value={value}
        checked={checked}
        disabled={disabled}
        onChange={() => onChange?.(value)}
        className="fi-radio__input"
      />
      <span className="fi-radio__dot" aria-hidden="true" />
      {label && <span className="fi-radio__label">{label}</span>}
    </label>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// RadioGroup
// ═════════════════════════════════════════════════════════════════════════════

export interface RadioOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface RadioGroupProps extends FieldWrapperProps {
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  name: string;
  inline?: boolean;
  disabled?: boolean;
}

/**
 * @example
 * <RadioGroup
 *   label="Control Type"
 *   name="type"
 *   options={[{ value: 'PREVENTIVE', label: 'Preventive' }, { value: 'DETECTIVE', label: 'Detective' }]}
 *   value={v}
 *   onChange={fn}
 *   inline
 * />
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  label, required, error, helper, helperText, noMargin, inline, className,
  options, value, onChange, name, inline: groupInline, disabled,
}) => {
  const id = useId();
  return (
    <FieldWrapper 
      label={label} 
      required={required} 
      error={error} 
      helper={helper} 
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
    >
      <div role="radiogroup" aria-labelledby={label ? id : undefined}
        className={cx('fi-radio-group', groupInline && 'fi-radio-group--inline')}>
        {options.map(opt => (
          <Radio
            key={opt.value}
            name={name}
            value={opt.value}
            label={opt.label}
            checked={value === opt.value}
            disabled={disabled || opt.disabled}
            onChange={onChange}
          />
        ))}
      </div>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Toggle (Switch)
// ═════════════════════════════════════════════════════════════════════════════

export interface ToggleProps extends Omit<CheckboxProps, 'indeterminate'> {}

/**
 * Switch-style toggle based on checkbox logic.
 */
export const Toggle: React.FC<ToggleProps> = ({
  checked, onChange, disabled, label, required, id: propId, name, value, error, helper, helperText, noMargin, inline, className,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;
  return (
    <FieldWrapper 
      label="" 
      required={false} 
      error={error} 
      helper={helper} 
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
    >
      <label htmlFor={id} className={cx('fi-toggle', disabled && 'fi-toggle--disabled')}>
        <input
          type="checkbox"
          id={id}
          name={name}
          value={value}
          checked={checked}
          disabled={disabled}
          onChange={e => onChange?.(e.target.checked)}
          className="fi-toggle__input"
          aria-invalid={error ? 'true' : undefined}
        />
        <span className="fi-toggle__track" aria-hidden="true">
          <span className="fi-toggle__thumb" />
        </span>
        {label && <span className="fi-toggle__label">{label}</span>}
      </label>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FileUpload
// ═════════════════════════════════════════════════════════════════════════════

export interface FileUploadProps extends FieldWrapperProps {
  onChange?: (files: FileList | null) => void;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  placeholder?: string;
  subtitle?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label, required, error, helper, helperText, noMargin, inline, className,
  onChange, accept, multiple, disabled,
  placeholder = 'Click to Upload File',
  subtitle = 'or Drag & Drop',
}) => {
  const id = useId();
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setDragActive(false);
    if (!disabled && e.dataTransfer.files) onChange?.(e.dataTransfer.files);
  };

  return (
    <FieldWrapper
      label={label}
      required={required}
      error={error}
      helper={helper}
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
    >
      <div
        className={cx('fi-upload', dragActive && 'fi-upload--active', disabled && 'fi-upload--disabled', error && 'fi--error')}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          className="fi-upload__input"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={e => onChange?.(e.target.files)}
        />
        <div className="fi-upload__icon-wrap">
          <Upload size={20} className="fi-upload__icon" />
        </div>
        <span className="fi-upload__text">{placeholder}</span>
        {subtitle && <span className="fi-upload__subtitle">{subtitle}</span>}
      </div>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// MultiSelect
// ═════════════════════════════════════════════════════════════════════════════

export interface MultiSelectProps extends FieldWrapperProps {
  options: SelectOption[];
  value: (string | number)[];
  onChange: (value: (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeVariant;
  'data-testid'?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  label, required, error, helper, helperText, noMargin, inline, className,
  options, value, onChange, placeholder = 'Select options…',
  disabled, size, 'data-testid': dataTestId,
}) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const selectedOptions = options.filter(o => value.includes(o.value));
  const filtered = query.trim()
    ? options.filter(o => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const update = () => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      const PANEL_MAX = 280;
      const spaceBelow = window.innerHeight - r.bottom - 8;
      const spaceAbove = r.top - 8;
      const openUp = spaceBelow < Math.min(PANEL_MAX, 200) && spaceAbove > spaceBelow;
      const maxHeight = Math.max(120, Math.min(PANEL_MAX, openUp ? spaceAbove : spaceBelow));
      const base: React.CSSProperties = { position: 'fixed', left: r.left, width: r.width, right: 'auto', zIndex: 10001, maxHeight, overflowY: 'auto' };
      setPanelStyle(openUp
        ? { ...base, bottom: window.innerHeight - r.top + 4 }
        : { ...base, top: r.bottom + 4 });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const inContainer = containerRef.current?.contains(e.target as Node);
      const inPanel = panelRef.current?.contains(e.target as Node);
      if (!inContainer && !inPanel) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggleOption = (val: string | number) => {
    const newValue = value.includes(val) ? value.filter(v => v !== val) : [...value, val];
    onChange(newValue);
  };

  return (
    <FieldWrapper 
      label={label} 
      required={required} 
      error={error} 
      helper={helper} 
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
    >
      <div ref={containerRef} className={cx('fi-multi-select', open && 'fi-multi-select--open', disabled && 'fi-multi-select--disabled')}>
        <div className="fi-multi-select__trigger" data-testid={dataTestId} onClick={() => !disabled && setOpen(!open)}>
          <div className="fi-multi-select__tags">
            {selectedOptions.length === 0 ? (
              <span className="fi-multi-select__placeholder">{placeholder}</span>
            ) : selectedOptions.map(opt => (
              <span key={opt.value} className="fi-multi-select__tag">
                {opt.label}
                <span className="fi-multi-select__tag-remove" onClick={(e) => { e.stopPropagation(); toggleOption(opt.value); }}>
                  <X size={10} />
                </span>
              </span>
            ))}
          </div>
          <ChevronDown size={14} className="fi-multi-select__chevron" />
        </div>
        {open && createPortal(
          <div ref={panelRef} className="fi-multi-select__panel" style={panelStyle}>
            <div className="fi-multi-select__search">
              <Search size={12} />
              <input
                type="text"
                placeholder="Search..."
                value={query}
                onChange={e => setQuery(e.target.value)}
                onClick={e => e.stopPropagation()}
                style={{ outline: 'none', boxShadow: 'none' }}
              />
            </div>
            <ul className="fi-multi-select__list">
              {filtered.length === 0 ? (
                <li className="fi-multi-select__empty">
                  {query.trim() ? 'No results found' : 'No options available'}
                </li>
              ) : filtered.map(opt => (
                <li
                  key={opt.value}
                  className={cx('fi-multi-select__option', value.includes(opt.value) && 'fi-multi-select__option--selected')}
                  onClick={(e) => { e.stopPropagation(); toggleOption(opt.value); }}
                >
                  <div className="fi-multi-select__checkbox">
                    {value.includes(opt.value) && <Check size={10} />}
                  </div>
                  <span>{opt.label}</span>
                </li>
              ))}
            </ul>
          </div>,
          document.body
        )}
      </div>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// DatePicker — wraps react-datepicker, keeps event-style onChange API for
// backward compatibility with existing callers (`onChange={(e) => fn(e.target.value)}`)
// ═════════════════════════════════════════════════════════════════════════════

export interface DatePickerProps extends FieldWrapperProps {
  /** ISO date string "YYYY-MM-DD" */
  value?: string;
  /** Receives a synthetic event-shaped object: { target: { value: string } } */
  onChange?: (e: { target: { value: string } }) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  name?: string;
  autoFocus?: boolean;
  tabIndex?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  minDate?: Date;
  maxDate?: Date;
  /** Format displayed in the input (default: entity date-format setting) */
  dateFormat?: string;
  /** Forwarded onto react-datepicker's <input> for test/crawl targeting. */
  'data-testid'?: string;
}

// react-datepicker does not forward arbitrary DOM props (e.g. data-testid) to its
// internal <input>. This thin customInput forwards everything react-datepicker
// injects (value/onChange/onClick/className/…) and just adds the data-testid, so
// the visual + masking behaviour are unchanged but the testid lands on the input.
const DpInput = React.forwardRef<HTMLInputElement, any>(({ 'data-testid': dataTestId, ...props }, ref) => (
  <input ref={ref} {...props} data-testid={dataTestId} />
));

const toIsoDateString = (date: Date | null): string => {
  if (!date) return '';
  // Local-date ISO (YYYY-MM-DD) — avoids UTC-shift bugs from toISOString()
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const parseIsoDate = (value?: string): Date | null => {
  if (!value) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/**
 * Format an ISO date string (YYYY-MM-DD, optionally with a time part) into the
 * entity's configured display format (ORG entity settings → date_format) — the
 * same format the DatePicker renders. Use this anywhere a stored date is shown
 * read-only (table cells, detail rows) so every date across the app reads
 * identically. Returns '—' for empty input.
 */
export const formatDate = (value?: string | null): string => formatDateBySetting(value);

export const DatePicker: React.FC<DatePickerProps> = ({
  label,
  required,
  error,
  helper,
  helperText,
  noMargin,
  inline,
  className,
  value,
  onChange,
  placeholder,
  disabled,
  id: propId,
  name,
  autoFocus,
  tabIndex,
  onBlur,
  onFocus,
  minDate,
  maxDate,
  dateFormat,
  'data-testid': dataTestId,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;
  const selected = parseIsoDate(value);
  // Default display format/placeholder follow the entity date-format setting;
  // an explicit dateFormat/placeholder prop still wins.
  const effectiveFormat = dateFormat ?? toDatePickerFormat();
  const effectivePlaceholder = placeholder ?? toDatePlaceholder();

  const handleChange = (date: Date | null) => {
    if (!onChange) return;
    onChange({ target: { value: toIsoDateString(date) } });
  };

  return (
    <FieldWrapper
      label={label}
      required={required}
      error={error}
      helper={helper}
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
      id={id}
    >
      <div className="fi-icon-wrap">
        <span className="fi-icon" aria-hidden="true"><Calendar size={14} /></span>
        <ReactDatePicker
          id={id}
          name={name}
          selected={selected}
          onChange={handleChange}
          dateFormat={effectiveFormat}
          placeholderText={effectivePlaceholder}
          disabled={disabled}
          autoFocus={autoFocus}
          tabIndex={tabIndex}
          onBlur={onBlur}
          onFocus={onFocus}
          minDate={minDate}
          maxDate={maxDate}
          className={cx('fi', 'fi--text', 'fi--datepicker', error && 'fi--error')}
          customInput={<DpInput data-testid={dataTestId} />}
          autoComplete="off"
          showPopperArrow={false}
          popperPlacement="bottom-start"
          calendarStartDay={1}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          popperContainer={({ children }: { children?: React.ReactNode }) => createPortal(children as any, document.body)}
          onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
            if (e.ctrlKey || e.metaKey || e.altKey) return;
            if (e.key.length === 1 && !/[0-9]/.test(e.key)) {
              e.preventDefault();
            }
          }}
          onChangeRaw={(e: any) => {
            if (!e || !e.target || typeof e.target.value !== 'string') return;
            let digits = e.target.value.replace(/\D/g, '').slice(0, 8);
            if (digits.length >= 2) {
              const d = parseInt(digits.slice(0, 2), 10);
              if (d > 31) digits = '31' + digits.slice(2);
              else if (d === 0) digits = '01' + digits.slice(2);
            }
            if (digits.length >= 4) {
              const m = parseInt(digits.slice(2, 4), 10);
              if (m > 12) digits = digits.slice(0, 2) + '12' + digits.slice(4);
              else if (m === 0) digits = digits.slice(0, 2) + '01' + digits.slice(4);
            }
            let formatted = digits;
            if (digits.length > 4) formatted = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
            else if (digits.length > 2) formatted = `${digits.slice(0, 2)}-${digits.slice(2)}`;
            if (formatted !== e.target.value) {
              e.target.value = formatted;
            }
          }}
        />
      </div>
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// TimePicker — custom scrollable hour/minute picker, matches DatePicker style
// ═════════════════════════════════════════════════════════════════════════════

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

export interface TimePickerProps extends FieldWrapperProps {
  /** "HH:mm" 24-hour string */
  value?: string;
  /** Receives { target: { value: string } } where value is "HH:mm" */
  onChange?: (e: { target: { value: string } }) => void;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  tabIndex?: number;
  onBlur?: () => void;
  onFocus?: () => void;
  /** "HH:mm" — times strictly before this are not selectable (e.g. "now" when the paired date is today). */
  minTime?: string;
  /** Forwarded onto the trigger so tests can use getByTestId. */
  'data-testid'?: string;
}

export const TimePicker: React.FC<TimePickerProps> = ({
  label,
  required,
  error,
  helper,
  helperText,
  noMargin,
  inline,
  className,
  value,
  onChange,
  placeholder = '--:--',
  disabled,
  id: propId,
  tabIndex,
  onBlur,
  onFocus,
  minTime,
  'data-testid': dataTestId,
}) => {
  const autoId = useId();
  const id = propId ?? autoId;
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const hourColRef = useRef<HTMLDivElement | null>(null);
  const minuteColRef = useRef<HTMLDivElement | null>(null);
  const [panelStyle, setPanelStyle] = useState<React.CSSProperties>({});

  const parts = value ? value.split(':') : [];
  const hour = parts[0] ?? '';
  const minute = parts[1] ?? '';

  const openPanel = useCallback(() => {
    if (disabled) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setPanelStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        zIndex: 10000,
      });
    }
    setOpen(true);
    onFocus?.();
  }, [disabled, onFocus]);

  useEffect(() => {
    if (!open) return;
    const scrollToSelected = (col: HTMLDivElement | null, val: string) => {
      const el = col?.querySelector(`[data-val="${val}"]`) as HTMLElement | null;
      el?.scrollIntoView({ block: 'nearest' });
    };
    const t = setTimeout(() => {
      scrollToSelected(hourColRef.current, hour || '00');
      scrollToSelected(minuteColRef.current, minute || '00');
    }, 20);
    return () => clearTimeout(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (
        !panelRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
        onBlur?.();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open, onBlur]);

  // Zero-padded "HH"/"mm" strings compare correctly as plain strings.
  const [minHour, minMinute] = minTime ? minTime.split(':') : ['', ''];
  const isHourDisabled = (h: string) => !!minHour && h < minHour;
  const isMinuteDisabled = (m: string) =>
    !!minHour && (hour || '00') === minHour && m < (minMinute || '00');

  const selectHour = useCallback(
    (h: string) => {
      let mm = minute || '00';
      // Landing exactly on the min hour with an earlier minute would produce a
      // value before minTime — clamp the minute up to the floor.
      if (minTime && h === minHour && mm < (minMinute || '00')) mm = minMinute || '00';
      onChange?.({ target: { value: `${h}:${mm}` } });
    },
    [minute, onChange, minTime, minHour, minMinute],
  );

  const selectMinute = useCallback(
    (m: string) => { onChange?.({ target: { value: `${hour || '00'}:${m}` } }); },
    [hour, onChange],
  );

  return (
    <FieldWrapper
      label={label}
      required={required}
      error={error}
      helper={helper}
      helperText={helperText}
      noMargin={noMargin}
      inline={inline}
      className={className}
      id={id}
    >
      <div className="fi-icon-wrap">
        <span className="fi-icon" aria-hidden="true"><Clock size={14} /></span>
        <div
          ref={triggerRef}
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          data-testid={dataTestId}
          tabIndex={disabled ? -1 : (tabIndex ?? 0)}
          className={cx('fi', 'fi--text', 'fi--timepicker', error && 'fi--error', disabled && 'fi--disabled')}
          onClick={openPanel}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPanel(); }
            if (e.key === 'Escape') { setOpen(false); onBlur?.(); }
          }}
        >
          {value ? (
            <span className="fi--timepicker__value">{value}</span>
          ) : (
            <span className="fi--timepicker__placeholder">{placeholder}</span>
          )}
        </div>
      </div>

      {open && createPortal(
        <div ref={panelRef} className="timepicker-panel" style={panelStyle}>
          <div className="timepicker-panel__cols">
            <div className="timepicker-panel__col" ref={hourColRef}>
              <div className="timepicker-panel__col-header">HR</div>
              {HOURS.map(h => {
                const dis = isHourDisabled(h);
                return (
                  <div
                    key={h}
                    data-val={h}
                    className={cx('timepicker-panel__item', h === hour && 'timepicker-panel__item--selected', dis && 'timepicker-panel__item--disabled')}
                    onClick={dis ? undefined : () => selectHour(h)}
                  >
                    {h}
                  </div>
                );
              })}
            </div>
            <div className="timepicker-panel__divider" />
            <div className="timepicker-panel__col" ref={minuteColRef}>
              <div className="timepicker-panel__col-header">MIN</div>
              {MINUTES.map(m => {
                const dis = isMinuteDisabled(m);
                return (
                  <div
                    key={m}
                    data-val={m}
                    className={cx('timepicker-panel__item', m === minute && 'timepicker-panel__item--selected', dis && 'timepicker-panel__item--disabled')}
                    onClick={dis ? undefined : () => selectMinute(m)}
                  >
                    {m}
                  </div>
                );
              })}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </FieldWrapper>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// FormControl Namespace
// ═════════════════════════════════════════════════════════════════════════════

export const FormControl = {
  Input,
  Select,
  Textarea,
  Radio,
  RadioGroup,
  Checkbox,
  SearchSelect,
  DatePicker,
  TimePicker,
  Toggle,
  FileUpload,
  MultiSelect,
  FormGroup,
  FieldWrapper,
  formatDate,
};

export default FormControl;
